import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { api } from "@/lib/api";

// Footer brand mark — CMS-driven via the INDEPENDENT `footer_logo`
// settings field (Sep 2026 nav/footer logo split). Empty setting falls
// back to the built-in UD. closing-signature monogram so existing
// branding never disappears. The navigation header is on its own
// separate `site_logo` path — the two never share state. Sizing stays
// controlled by these exact CSS classes (140px desktop / 95px mobile
// wide, height auto) — no dashboard size controls by design.
const DEFAULT_FOOTER_LOGO = "/brand/ud-monogram.png";
const DEFAULT_FOOTER_LOGO_WIDTH_DESKTOP = 140;
const DEFAULT_FOOTER_LOGO_WIDTH_MOBILE = 95;

const FooterBrandMark = ({ src, desktopWidth, mobileWidth }) => {
  // Width-only, CMS-controlled (footer_logo_width_desktop /
  // footer_logo_width_mobile). Height stays `auto` so the asset's
  // aspect ratio is always preserved. Empty/invalid settings fall back
  // to the approved live defaults (140 desktop / 95 mobile). Sizing is
  // applied via CSS custom properties + the .ur-footer-logo rules below
  // so the md breakpoint behaves exactly like the previous Tailwind
  // w-[95px] md:w-[140px] classes — spacing and alignment untouched.
  const dw = Number(desktopWidth) || DEFAULT_FOOTER_LOGO_WIDTH_DESKTOP;
  const mw = Number(mobileWidth) || DEFAULT_FOOTER_LOGO_WIDTH_MOBILE;
  return (
    <>
      <style>{`
        .ur-footer-logo { width: var(--flw-m, ${DEFAULT_FOOTER_LOGO_WIDTH_MOBILE}px); }
        @media (min-width: 768px) { .ur-footer-logo { width: var(--flw-d, ${DEFAULT_FOOTER_LOGO_WIDTH_DESKTOP}px); } }
      `}</style>
      <img
        src={src || DEFAULT_FOOTER_LOGO}
        alt="Ur Designs"
        data-testid="footer-brand-mark"
        className="ur-footer-logo block h-auto select-none"
        style={{ "--flw-m": `${mw}px`, "--flw-d": `${dw}px` }}
        draggable={false}
        loading="lazy"
        decoding="async"
      />
    </>
  );
};

// Footer — Phase Home Page Positioning Revision restructure.
// Columns now reflect the three business lines (Services / Workstations /
// Software) plus a compact Company column, with contact + social retained
// in the rightmost column.
//
// Website Repositioning (Sep 2026): the Workstations + Software columns
// (desktop) and their mobile links only render while the CMS flag
// `public_commerce_workstations_software_enabled` is ON. The link data,
// column markup and brand description are preserved for reactivation.
export const Footer = () => {
  // Oct 2026: on the HOMEPAGE ONLY, the footer container joins the shared
  // large-desktop master grid (min-[1440px]: 1720px + clamp gutters) so it
  // aligns with the Final CTA / hero / sections above it. Other routes are
  // untouched — their content still uses the 1400px container, and the
  // footer must not outrun them. Below 1440px this changes nothing
  // (footer already shares the 1400px laptop grid with the sections).
  const { pathname } = useLocation();
  const onMasterGrid = pathname === "/";
  const [s, setS] = useState({});
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
  }, []);
  const email = s.contact_email || "solutions@allurdesigns.com";
  const phone = s.contact_phone || "+297 593-2727";
  const address = s.contact_address || "Palm Beach, Aruba";
  const commerceOn = s.public_commerce_workstations_software_enabled === true;

  const services = [
    { label: "Logo & Brand Identity",             to: "/services/logo-brand-identity" },
    { label: "Advertising, Campaigns & Motion",   to: "/services/advertising-campaigns-motion" },
    { label: "Event Creative & Promotion",        to: "/services/event-creative-promotion" },
    { label: "Business & Marketing Design",       to: "/services/business-marketing-design" },
    { label: "Packaging, Labels & Vehicle Wraps", to: "/services/packaging-labels-vehicle-wraps" },
    { label: "3D Rendering & Visualization",      to: "/services/3d-rendering-visualization" },
    { label: "View All Services",                 to: "/services" },
  ];
  const workstations = [
    { label: "Business & Productivity", to: "/workstations/business_productivity" },
    { label: "Content & Marketing", to: "/workstations/content_marketing" },
    { label: "Creative & Design", to: "/workstations/creative_design" },
    { label: "Architecture & 3D", to: "/workstations/architecture_3d" },
    // Volume inquiry — deep-link to the section anchor on /workstations.
    { label: "Teams & Organizations", to: "/workstations#volume-laptops-section" },
  ];
  const software = [
    { label: "Creative & Design", to: "/software?category=creative" },
    { label: "Architecture & 3D", to: "/software?category=architecture" },
    { label: "Business & Productivity", to: "/software?category=business" },
    { label: "Browse Software", to: "/software" },
  ];
  const company = [
    { label: "Portfolio", to: "/portfolio" },
    { label: "About", to: "/about" },
    { label: "Contact", to: "/contact" },
  ];

  return (
    <footer
      data-testid="main-footer"
      className="bg-[#0A0A0A] border-t border-[#1F1F1F]"
    >
      <div className={`max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16${onMasterGrid ? " min-[1440px]:max-w-[1720px] min-[1440px]:px-[clamp(2.5rem,4vw,4rem)]" : ""}`}>
        {/* Desktop / tablet footer (≥md) — full 4-column sitemap
            with the 7 canonical service-category links. Preserved
            per approved design. Grid collapses to 8 columns while
            the commerce columns are hidden (3 brand + 2 services +
            3 company). */}
        <div className={`hidden md:grid grid-cols-2 ${commerceOn ? "md:grid-cols-12" : "md:grid-cols-8"} gap-10`} data-testid="footer-desktop">
          {/* Brand + intro */}
          <div className="col-span-2 md:col-span-3">
            <FooterBrandMark src={s.footer_logo} desktopWidth={s.footer_logo_width_desktop} mobileWidth={s.footer_logo_width_mobile} />
            <p className="mt-6 text-sm text-[#8F8F8F] leading-relaxed max-w-xs">
              Ur Designs is a brand and creative studio founded in Aruba
              in 2005 — from complete brand identities to everyday
              design, campaigns, packaging and 3D visualization, for
              businesses anywhere.
            </p>
          </div>

          {/* Services */}
          <div className="md:col-span-2" data-testid="footer-col-services">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white">
              Services
            </h4>
            <ul className="mt-5 space-y-3 text-sm text-[#8F8F8F]">
              {services.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="hover:text-[#FF6600] leading-snug inline-block"
                    data-testid={`footer-service-link-${l.to.replace(/\W+/g, "-").replace(/^-+|-+$/g, "")}`}
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Workstations */}
          {commerceOn && (
            <div className="md:col-span-2" data-testid="footer-col-workstations">
              <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white">
                Workstations
              </h4>
              <ul className="mt-5 space-y-3 text-sm text-[#8F8F8F]">
                {workstations.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="hover:text-[#FF6600]">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Software */}
          {commerceOn && (
            <div className="md:col-span-2" data-testid="footer-col-software">
              <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white">
                Software
              </h4>
              <ul className="mt-5 space-y-3 text-sm text-[#8F8F8F]">
                {software.map((l) => (
                  <li key={l.label}>
                    <Link to={l.to} className="hover:text-[#FF6600]">
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Company + Contact + Social */}
          <div className="md:col-span-3" data-testid="footer-col-company">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white">
              Company
            </h4>
            <ul className="mt-5 space-y-3 text-sm text-[#8F8F8F]">
              {company.map((l) => (
                <li key={l.label}>
                  <Link to={l.to} className="hover:text-[#FF6600]">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="mt-8 text-xs font-bold uppercase tracking-[0.2em] text-white">
              Contact
            </h4>
            <ul className="mt-5 space-y-3 text-sm text-[#8F8F8F]">
              <li>
                <a
                  data-testid="footer-email"
                  href={`mailto:${email}`}
                  className="hover:text-[#FF6600]"
                >
                  {email}
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-phone"
                  href={`tel:${phone.replace(/[^+\d]/g, "")}`}
                  className="hover:text-[#FF6600]"
                >
                  {phone}
                </a>
              </li>
              <li data-testid="footer-address">{address}</li>
            </ul>
            {(s.social_facebook || s.social_instagram || s.social_linkedin) && (
              <ul
                data-testid="footer-social"
                className="mt-6 flex flex-wrap gap-x-4 gap-y-2 text-sm text-[#8F8F8F]"
              >
                {s.social_facebook && (
                  <li>
                    <a
                      data-testid="footer-facebook"
                      href={s.social_facebook}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-[#FF6600]"
                    >
                      Facebook
                    </a>
                  </li>
                )}
                {s.social_instagram && (
                  <li>
                    <a
                      data-testid="footer-instagram"
                      href={s.social_instagram}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-[#FF6600]"
                    >
                      Instagram
                    </a>
                  </li>
                )}
                {s.social_linkedin && (
                  <li>
                    <a
                      data-testid="footer-linkedin"
                      href={s.social_linkedin}
                      target="_blank"
                      rel="noreferrer"
                      className="hover:text-[#FF6600]"
                    >
                      LinkedIn
                    </a>
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>

        {/* Mobile footer (<md) — a deliberately simplified single-
            column navigation. The 7 individual service-category
            links are NOT duplicated here; /services already gives
            mobile visitors a dedicated Finder + full 13-item
            deliverable index. Long service names in a narrow
            sidebar column produced a wall of wrapped text on
            390–420px viewports, so we keep the mobile footer
            concise and give Contact full width. */}
        <div className="md:hidden space-y-10" data-testid="footer-mobile">
          <div>
            <FooterBrandMark src={s.footer_logo} desktopWidth={s.footer_logo_width_desktop} mobileWidth={s.footer_logo_width_mobile} />
            <p className="mt-5 text-sm text-[#8F8F8F] leading-relaxed">
              Ur Designs is a brand and creative studio founded in Aruba
              in 2005 — from complete brand identities to everyday
              design, campaigns, packaging and 3D visualization, for
              businesses anywhere.
            </p>
          </div>

          <div data-testid="footer-mobile-col-services">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white">Services</h4>
            <ul className="mt-4 space-y-3 text-sm text-[#8F8F8F]">
              <li><Link data-testid="footer-mobile-link-services" to="/services" className="hover:text-[#FF6600]">Services</Link></li>
              {commerceOn && (
                <li><Link data-testid="footer-mobile-link-workstations" to="/workstations" className="hover:text-[#FF6600]">Workstations</Link></li>
              )}
              {commerceOn && (
                <li><Link data-testid="footer-mobile-link-software" to="/software" className="hover:text-[#FF6600]">Software</Link></li>
              )}
              <li><Link data-testid="footer-mobile-link-portfolio" to="/portfolio" className="hover:text-[#FF6600]">Portfolio</Link></li>
            </ul>
          </div>

          <div data-testid="footer-mobile-col-company">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white">Company</h4>
            <ul className="mt-4 space-y-3 text-sm text-[#8F8F8F]">
              <li><Link data-testid="footer-mobile-link-about" to="/about" className="hover:text-[#FF6600]">About</Link></li>
              <li><Link data-testid="footer-mobile-link-contact" to="/contact" className="hover:text-[#FF6600]">Contact</Link></li>
            </ul>
          </div>

          <div data-testid="footer-mobile-col-contact">
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-white">Contact</h4>
            <ul className="mt-4 space-y-3 text-sm text-[#8F8F8F]">
              <li>
                <a
                  data-testid="footer-mobile-email"
                  href={`mailto:${email}`}
                  className="hover:text-[#FF6600] break-all"
                >
                  {email}
                </a>
              </li>
              <li>
                <a
                  data-testid="footer-mobile-phone"
                  href={`tel:${phone.replace(/[^+\d]/g, "")}`}
                  className="hover:text-[#FF6600]"
                >
                  {phone}
                </a>
              </li>
              <li data-testid="footer-mobile-address">{address}</li>
            </ul>
            {(s.social_facebook || s.social_instagram || s.social_linkedin) && (
              <ul
                data-testid="footer-mobile-social"
                className="mt-5 flex flex-wrap gap-x-4 gap-y-2 text-sm text-[#8F8F8F]"
              >
                {s.social_facebook && (
                  <li><a data-testid="footer-mobile-facebook" href={s.social_facebook} target="_blank" rel="noreferrer" className="hover:text-[#FF6600]">Facebook</a></li>
                )}
                {s.social_instagram && (
                  <li><a data-testid="footer-mobile-instagram" href={s.social_instagram} target="_blank" rel="noreferrer" className="hover:text-[#FF6600]">Instagram</a></li>
                )}
                {s.social_linkedin && (
                  <li><a data-testid="footer-mobile-linkedin" href={s.social_linkedin} target="_blank" rel="noreferrer" className="hover:text-[#FF6600]">LinkedIn</a></li>
                )}
              </ul>
            )}
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-[#1F1F1F] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-xs text-[#666]">
            © {new Date().getFullYear()} Ur Designs. All rights reserved.
          </div>
          <Link
            to="/admin"
            data-testid="admin-link"
            className="text-xs text-[#666] hover:text-[#FF6600]"
          >
            Admin
          </Link>
        </div>
      </div>
    </footer>
  );
};
